import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckSquare,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Square,
  Check,
  Pencil,
  ArrowLeft,
  Clock,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type {
  Checklist,
  ChecklistSection as CSection,
  ChecklistItem,
  ChecklistItemStatus,
} from '@peles/shared';
import {
  useChecklists,
  useCreateChecklist,
  useDeleteChecklist,
  useUpdateChecklist,
  useAddChecklistSection,
  useDeleteChecklistSection,
  useAddChecklistItem,
  useUpdateChecklistItem,
  useDeleteChecklistItem,
  useToggleChecklistItem,
} from '../hooks/useEventOps';
import { SaveAsTemplateButton } from '../components/event-ops/SaveAsTemplateButton';
import { SearchableListSelect } from '../components/common/SearchableListSelect';
import * as usersApi from '../api/users';
import { useDialog } from '../context/DialogContext';
import type { User } from '@peles/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Assignee {
  id: string;
  name: string;
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_BADGE_CLASSES: Record<ChecklistItemStatus, string> = {
  TODO: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  DONE: 'bg-green-100 text-green-700',
  BLOCKED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<ChecklistItemStatus, string> = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
  BLOCKED: 'Blocked',
};

function StatusBadge({ status }: { status: ChecklistItemStatus }) {
  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_BADGE_CLASSES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── Inline Title Editor ──────────────────────────────────────────────────────

function InlineTitleEditor({
  value,
  onSave,
  className = '',
}: {
  value: string;
  onSave: (v: string) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation();
    setDraft(value);
    setEditing(true);
  }

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={(e) => e.stopPropagation()}
        className={`border border-primary-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 ${className}`}
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      className={`flex items-center gap-1 group text-left hover:text-primary-700 transition-colors ${className}`}
      title="Click to rename"
    >
      <span className="font-medium">{value}</span>
      <Pencil
        size={12}
        className="text-gray-300 group-hover:text-primary-500 shrink-0 transition-colors"
      />
    </button>
  );
}

// ─── Item Form ────────────────────────────────────────────────────────────────

interface ItemFormState {
  title: string;
  status: ChecklistItemStatus;
  dueDate: string;
  dueDateOffsetDays: string;
  assignedToUserId: string;
  notes: string;
}

const EMPTY_ITEM_FORM: ItemFormState = {
  title: '',
  status: 'TODO',
  dueDate: '',
  dueDateOffsetDays: '',
  assignedToUserId: '',
  notes: '',
};

function ItemForm({
  initial,
  isTemplate,
  assignees,
  assigneesLoading,
  onSubmit,
  onCancel,
  submitLabel = 'Add',
}: {
  initial?: Partial<ItemFormState>;
  isTemplate: boolean;
  assignees: Assignee[];
  assigneesLoading?: boolean;
  onSubmit: (form: ItemFormState) => void;
  onCancel: () => void;
  submitLabel?: string;
}) {
  const [form, setForm] = useState<ItemFormState>({ ...EMPTY_ITEM_FORM, ...initial });

  function set(field: keyof ItemFormState, val: string) {
    setForm((f) => ({ ...f, [field]: val }));
  }

  return (
    <div className="mt-2 space-y-2 p-3 bg-gray-50 rounded-md border border-gray-200">
      <input
        type="text"
        value={form.title}
        onChange={(e) => set('title', e.target.value)}
        placeholder="Task title"
        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        autoFocus
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Status</label>
          <select
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="TODO">To Do</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="DONE">Done</option>
            <option value="BLOCKED">Blocked</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Assignee</label>
          <SearchableListSelect
            items={assignees}
            value={form.assignedToUserId || null}
            onChange={(val) => set('assignedToUserId', typeof val === 'string' ? val : '')}
            getValue={(a) => a.id}
            getLabel={(a) => a.name}
            placeholder="Unassigned"
            clearable
            isLoading={assigneesLoading}
            ariaLabel="Select assignee"
          />
        </div>
      </div>
      {isTemplate ? (
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">
            Days offset from event (negative = before event)
          </label>
          <input
            type="number"
            value={form.dueDateOffsetDays}
            onChange={(e) => set('dueDateOffsetDays', e.target.value)}
            placeholder="e.g. -7 (7 days before event)"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      ) : (
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Due date</label>
          <input
            type="date"
            value={form.dueDate}
            onChange={(e) => set('dueDate', e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      )}
      <div>
        <label className="block text-xs text-gray-500 mb-0.5">Notes</label>
        <input
          type="text"
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Optional notes"
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onSubmit(form)}
          disabled={!form.title.trim()}
          className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Due Date Display ─────────────────────────────────────────────────────────

function DueDateBadge({
  dueDate,
  dueDateOffsetDays,
  isTemplate,
}: {
  dueDate: Date | null;
  dueDateOffsetDays: number | null;
  isTemplate: boolean;
}) {
  if (isTemplate && dueDateOffsetDays != null) {
    const label =
      dueDateOffsetDays === 0
        ? 'Event day'
        : dueDateOffsetDays > 0
          ? `+${dueDateOffsetDays}d after`
          : `${Math.abs(dueDateOffsetDays)}d before`;
    return (
      <span className="flex items-center gap-0.5 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
        <Clock size={10} />
        {label}
      </span>
    );
  }
  if (dueDate) {
    const d = new Date(dueDate);
    // Treat as a calendar date stored at UTC midnight; convert to local calendar date
    const localDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isOverdue = localDate < today;
    const fmt = localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return (
      <span
        className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${
          isOverdue ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'
        }`}
      >
        <Clock size={10} />
        {fmt}
      </span>
    );
  }
  return null;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function TaskListPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { data: checklists = [], isLoading } = useChecklists(eventId);
  const createChecklist = useCreateChecklist();
  const deleteChecklist = useDeleteChecklist();
  const updateChecklist = useUpdateChecklist();
  const addSection = useAddChecklistSection();
  const deleteSection = useDeleteChecklistSection();
  const addItem = useAddChecklistItem();
  const updateItem = useUpdateChecklistItem();
  const deleteItem = useDeleteChecklistItem();
  const toggleItem = useToggleChecklistItem();

  const [expandedChecklist, setExpandedChecklist] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [newSectionName, setNewSectionName] = useState('');
  const [addingItemSection, setAddingItemSection] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const isEventScoped = !!eventId;

  // Always fetch all active users for the assignee dropdown
  const { data: allUsers = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers({ isActive: true }),
  });

  const assignees: Assignee[] = allUsers.map((u) => ({ id: u.id, name: u.name }));

  const handleCreateChecklist = useCallback(() => {
    createChecklist.mutate({ eventId, title: 'New Task List' });
  }, [createChecklist, eventId]);

  const handleDeleteChecklist = useCallback(
    async (id: string) => {
      if (
        await confirm({
          title: 'Delete Task List',
          message: 'Delete this task list?',
          confirmLabel: 'Delete',
        })
      ) {
        deleteChecklist.mutate(id);
      }
    },
    [deleteChecklist, confirm],
  );

  const handleRenameChecklist = useCallback(
    (id: string, title: string) => {
      updateChecklist.mutate({ id, updates: { title } });
    },
    [updateChecklist],
  );

  const toggleChecklistExpand = useCallback((id: string) => {
    setExpandedChecklist((prev) => (prev === id ? null : id));
  }, []);

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddSection = useCallback(
    (checklistId: string) => {
      if (!newSectionName.trim()) return;
      addSection.mutate({ checklistId, name: newSectionName.trim() });
      setNewSectionName('');
    },
    [addSection, newSectionName],
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      deleteSection.mutate(sectionId);
    },
    [deleteSection],
  );

  function buildItemPayload(form: ItemFormState, isTemplate: boolean) {
    return {
      title: form.title.trim(),
      status: form.status as ChecklistItemStatus,
      assignedToUserId: form.assignedToUserId || undefined,
      notes: form.notes || undefined,
      dueDate: !isTemplate && form.dueDate ? form.dueDate : undefined,
      dueDateOffsetDays:
        isTemplate && form.dueDateOffsetDays !== ''
          ? parseInt(form.dueDateOffsetDays, 10)
          : undefined,
    };
  }

  const handleAddItem = useCallback(
    (sectionId: string, form: ItemFormState, isTemplate: boolean) => {
      if (!form.title.trim()) return;
      addItem.mutate({
        sectionId,
        ...buildItemPayload(form, isTemplate),
      });
      setAddingItemSection(null);
    },
    [addItem],
  );

  const handleUpdateItem = useCallback(
    (itemId: string, form: ItemFormState, isTemplate: boolean) => {
      if (!form.title.trim()) return;
      updateItem.mutate({
        itemId,
        updates: {
          ...buildItemPayload(form, isTemplate),
          dueDate: !isTemplate ? form.dueDate || null : undefined,
          dueDateOffsetDays: isTemplate
            ? form.dueDateOffsetDays !== ''
              ? parseInt(form.dueDateOffsetDays, 10)
              : null
            : undefined,
        },
      });
      setEditingItemId(null);
    },
    [updateItem],
  );

  const handleToggleItem = useCallback(
    (itemId: string, isDone: boolean) => {
      toggleItem.mutate({ itemId, isDone: !isDone });
    },
    [toggleItem],
  );

  const handleDeleteItem = useCallback(
    (itemId: string) => {
      deleteItem.mutate(itemId);
    },
    [deleteItem],
  );

  function getSectionProgress(section: CSection): { done: number; total: number } {
    const total = section.items.length;
    const done = section.items.filter((i) => i.isDone).length;
    return { done, total };
  }

  function itemToFormState(item: ChecklistItem): ItemFormState {
    const dueDate = item.dueDate ? new Date(item.dueDate).toISOString().slice(0, 10) : '';
    return {
      title: item.title,
      status: item.status as ChecklistItemStatus,
      dueDate,
      dueDateOffsetDays: item.dueDateOffsetDays != null ? String(item.dueDateOffsetDays) : '',
      assignedToUserId: item.assignedToUserId ?? '',
      notes: item.notes ?? '',
    };
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading task lists...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {isEventScoped && (
            <button
              onClick={() => navigate(`/events/${eventId}`)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
              aria-label="Back to event"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back to Event</span>
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Task Lists</h1>
        </div>
        <button
          onClick={handleCreateChecklist}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">New Task List</span>
        </button>
      </div>

      {checklists.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">No task lists yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {checklists.map((checklist: Checklist) => (
            <div
              key={checklist.id}
              className="bg-white rounded-lg shadow"
              data-testid={`checklist-${checklist.id}`}
            >
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleChecklistExpand(checklist.id)}
              >
                <div className="flex items-center gap-2">
                  {expandedChecklist === checklist.id ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <CheckSquare size={18} className="text-green-600" />
                  <InlineTitleEditor
                    value={checklist.title || 'Untitled Task List'}
                    onSave={(title) => handleRenameChecklist(checklist.id, title)}
                  />
                  <span className="text-xs text-gray-400">
                    ({checklist.sections.length} sections)
                  </span>
                  {checklist.isTemplate && (
                    <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                      Template
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {!checklist.isTemplate && (
                    <SaveAsTemplateButton
                      id={checklist.id}
                      type="checklist"
                      defaultName={`${checklist.title || 'Task List'} Template`}
                    />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChecklist(checklist.id);
                    }}
                    className="text-gray-400 hover:text-red-600 p-1"
                    aria-label="Delete checklist"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {expandedChecklist === checklist.id && (
                <div className="border-t border-gray-200 px-4 py-4">
                  {checklist.sections.map((section: CSection) => {
                    const progress = getSectionProgress(section);
                    return (
                      <div
                        key={section.id}
                        className="mb-3 border border-gray-200 rounded-md"
                        data-testid={`section-${section.id}`}
                      >
                        <div
                          className="flex items-center justify-between px-3 py-2 bg-gray-50 cursor-pointer"
                          onClick={() => toggleSection(section.id)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedSections.has(section.id) ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )}
                            <span className="text-sm font-medium">{section.name}</span>
                            {section.nameHe && (
                              <span className="text-xs text-gray-400">{section.nameHe}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs text-gray-500"
                              data-testid={`progress-${section.id}`}
                            >
                              {progress.done}/{progress.total}
                            </span>
                            {progress.total > 0 && (
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 rounded-full"
                                  style={{
                                    width: `${(progress.done / progress.total) * 100}%`,
                                  }}
                                  data-testid={`progress-bar-${section.id}`}
                                />
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSection(section.id);
                              }}
                              className="text-gray-400 hover:text-red-600 p-0.5"
                              aria-label="Delete section"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {expandedSections.has(section.id) && (
                          <div className="px-3 py-2">
                            <div className="space-y-1">
                              {section.items.map((item: ChecklistItem) => (
                                <div key={item.id}>
                                  {editingItemId === item.id ? (
                                    <ItemForm
                                      initial={itemToFormState(item)}
                                      isTemplate={checklist.isTemplate}
                                      assignees={assignees}
                                      assigneesLoading={usersLoading}
                                      onSubmit={(form) =>
                                        handleUpdateItem(item.id, form, checklist.isTemplate)
                                      }
                                      onCancel={() => setEditingItemId(null)}
                                      submitLabel="Save"
                                    />
                                  ) : (
                                    <div
                                      className="flex items-start gap-2 py-1.5 group"
                                      data-testid={`item-${item.id}`}
                                    >
                                      <button
                                        onClick={() => handleToggleItem(item.id, item.isDone)}
                                        className="shrink-0 mt-0.5"
                                        aria-label={item.isDone ? 'Mark undone' : 'Mark done'}
                                      >
                                        {item.isDone ? (
                                          <Check size={16} className="text-green-600" />
                                        ) : (
                                          <Square size={16} className="text-gray-400" />
                                        )}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span
                                            className={`text-sm ${item.isDone ? 'line-through text-gray-400' : ''}`}
                                          >
                                            {item.title}
                                          </span>
                                          <StatusBadge
                                            status={item.status as ChecklistItemStatus}
                                          />
                                          <DueDateBadge
                                            dueDate={item.dueDate}
                                            dueDateOffsetDays={item.dueDateOffsetDays}
                                            isTemplate={checklist.isTemplate}
                                          />
                                          {(item.assignedToName || item.assignee) && (
                                            <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                              {item.assignedToName || item.assignee}
                                            </span>
                                          )}
                                        </div>
                                        {item.notes && (
                                          <p className="text-xs text-gray-400 mt-0.5">
                                            {item.notes}
                                          </p>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={() => setEditingItemId(item.id)}
                                          className="text-gray-400 hover:text-primary-600 p-0.5"
                                          aria-label="Edit item"
                                        >
                                          <Pencil size={12} />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteItem(item.id)}
                                          className="text-gray-400 hover:text-red-600 p-0.5"
                                          aria-label="Delete item"
                                        >
                                          <Trash2 size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {addingItemSection === section.id ? (
                              <ItemForm
                                isTemplate={checklist.isTemplate}
                                assignees={assignees}
                                onSubmit={(form) =>
                                  handleAddItem(section.id, form, checklist.isTemplate)
                                }
                                onCancel={() => setAddingItemSection(null)}
                              />
                            ) : (
                              <button
                                onClick={() => setAddingItemSection(section.id)}
                                className="mt-2 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                              >
                                <Plus size={12} /> Add Item
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add Section */}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="New section name"
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <button
                      onClick={() => handleAddSection(checklist.id)}
                      disabled={!newSectionName.trim()}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 disabled:opacity-50"
                    >
                      Add Section
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Re-export for backward compatibility
export { TaskListPage as ChecklistPage };
